import React from 'react';
import { Shield, Link2, Lock } from 'lucide-react';
import { GhostLogo } from './GhostLogo';

interface LandingPageProps {
    onGoToAdmin: () => void;
}

export function LandingPage({ onGoToAdmin }: LandingPageProps) {
    return (
        <div className="min-h-screen bg-cyber-dark cyber-grid relative overflow-hidden">
            {/* Floating neural nodes */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-20 left-[10%] w-2 h-2 rounded-full bg-cyan-400/40 animate-node-pulse" />
                <div className="absolute top-40 right-[15%] w-3 h-3 rounded-full bg-cyan-400/30 animate-node-pulse-delayed" />
                <div className="absolute top-[60%] left-[20%] w-2 h-2 rounded-full bg-cyan-400/20 animate-node-pulse" />
                <div className="absolute top-[30%] right-[25%] w-1.5 h-1.5 rounded-full bg-cyan-400/50 animate-node-pulse-delayed" />
                <div className="absolute bottom-[30%] left-[40%] w-2 h-2 rounded-full bg-cyan-400/30 animate-node-pulse" />
                <div className="absolute top-[15%] left-[60%] w-1.5 h-1.5 rounded-full bg-cyan-400/40 animate-node-pulse-delayed" />
                <div className="absolute bottom-[15%] right-[35%] w-2.5 h-2.5 rounded-full bg-cyan-400/25 animate-node-pulse" />
                {/* Glow orbs */}
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/3 rounded-full blur-3xl" />
            </div>

            {/* Header */}
            <header className="relative z-10 p-6">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center animate-cyber-glow">
                            <GhostLogo size={22} variant="solid" />
                        </div>
                        <span className="text-xl font-bold text-white">Messenger<span className="text-cyan-400">Phantom</span></span>
                    </div>
                    <div className="flex items-center gap-3">
                        <a
                            href="/register"
                            className="px-5 py-2 border border-cyan-500/30 hover:border-cyan-400/60 text-cyan-400 font-medium rounded-lg transition-all hover:bg-cyan-500/5"
                        >
                            Register
                        </a>
                        <button
                            onClick={onGoToAdmin}
                            className="px-5 py-2 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 font-medium rounded-lg transition-all"
                        >
                            Sign In
                        </button>
                    </div>
                </div>
            </header>

            {/* Hero Section */}
            <main className="relative z-10 max-w-6xl mx-auto px-6 py-20">
                <div className="text-center mb-16">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-500/20 bg-cyan-500/5 text-cyan-400 text-sm mb-8 animate-float">
                        <Lock className="w-3.5 h-3.5" />
                        Advanced Session Management
                    </div>
                    <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                        Session
                        <span className="text-cyan-400 animate-text-glow"> Intelligence</span>
                        <br />
                        <span className="text-gray-500 text-4xl md:text-5xl">Platform</span>
                    </h1>
                    <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-10">
                        Create tracking links, manage multi-platform sessions, and control
                        user connections through a secure admin interface.
                    </p>
                    <div className="flex items-center justify-center gap-4">
                        <button
                            onClick={onGoToAdmin}
                            className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 text-black font-semibold rounded-xl text-lg transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(0,212,255,0.3)]"
                        >
                            Get Started
                        </button>
                        <a
                            href="/register"
                            className="px-8 py-4 border border-cyan-500/30 hover:border-cyan-400/60 text-cyan-400 font-semibold rounded-xl text-lg transition-all hover:bg-cyan-500/5"
                        >
                            Create Account
                        </a>
                    </div>
                </div>

                {/* Features */}
                <div className="grid md:grid-cols-2 gap-6 mt-24 max-w-3xl mx-auto">
                    <div className="glass-card rounded-2xl p-8 transition-all duration-300 hover:border-cyan-500/30 group">
                        <div className="w-14 h-14 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-6 group-hover:animate-cyber-glow transition-all">
                            <Link2 className="w-7 h-7 text-cyan-400" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-3">Tracking Links</h3>
                        <p className="text-gray-500">
                            Create customized tracking links with QR code or phone number login options across multiple platforms.
                        </p>
                    </div>

                    <div className="glass-card rounded-2xl p-8 transition-all duration-300 hover:border-cyan-500/30 group">
                        <div className="w-14 h-14 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-6 group-hover:animate-cyber-glow transition-all">
                            <Shield className="w-7 h-7 text-cyan-400" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-3">Admin Dashboard</h3>
                        <p className="text-gray-500">
                            Manage everything from a secure admin interface with role-based access control.
                        </p>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="relative z-10 p-6 text-center text-gray-600 text-sm mt-20">
                © 2026 MessengerPhantom. All rights reserved.
            </footer>
        </div>
    );
}
